import { Actions, DockLocation, Model, TabNode, TabSetNode, type Action, type IJsonModel, type IJsonTabNode } from "flexlayout-react";
import { isAppPath, pathnameOf } from "../workspaceAddress";
import type { AppPath, TabId } from "../workspaceTypes";

/**
 * The tab layout (F90, WA-10): FlexLayout's model holds the groups, splits, order and
 * selection, and is the one owner of that state. DMW keeps only what is its own in each
 * tab: the route it shows and when it opened. This file and `TabLayoutView.tsx` are the
 * only code that knows the library.
 */
export interface TabPlace {
  readonly route: AppPath;
  /** When the tab opened, in ms: a section click focuses the newest tab of its kind (Q3). */
  readonly opened: number;
  /** What the page calls itself (`useTabTitle`), kept so a restored tab is named before it shows. */
  readonly title?: string;
  /** The tab's sidebar is closed; open until the user closes it (UX-3). Each tab keeps its own (F90). */
  readonly sidebarClosed?: true;
}

/**
 * FlexLayout's own maximize is off: with renaming off, a double-click on a tab reached its strip
 * and maximized the group, which hid the others and took no split from a drag (29 Oct). The one
 * group alone is the narrow screen's view, which `oneGroupShown` keeps.
 */
const LAYOUT_SETTINGS: IJsonModel["global"] = {
  tabEnableRename: false,
  tabEnablePopout: false,
  tabEnableFloat: false,
  tabSetEnableMaximize: false,
  tabSetEnableTabGroups: false,
  tabSetMinWidth: 280,
  tabSetMinHeight: 160,
};

/** The last opening time given out: each tab opens strictly after the one before, so "newest" never ties. */
let lastOpened = 0;

function openedNow(): number {
  lastOpened = Math.max(Date.now(), lastOpened + 1);
  return lastOpened;
}

/** A tab's name is its page's title, or its route until the page names itself; `TabLayoutView` words a route. */
function tabJson(id: TabId, route: AppPath): IJsonTabNode {
  const config: TabPlace = { route, opened: openedNow() };
  return { type: "tab", id, name: route, component: "page", config };
}

export function newTabId(): TabId {
  return `tab-${crypto.randomUUID()}`;
}

/** One group with one tab on `route`. */
export function freshLayout(route: AppPath): Model {
  return Model.fromJson({
    global: LAYOUT_SETTINGS,
    layout: { type: "row", children: [{ type: "tabset", active: true, children: [tabJson(newTabId(), route)] }] },
  });
}

/** A saved layout from its JSON, or `null` when there is none or it can't be read: then the app starts fresh, never blocked. */
export function restoredLayout(saved: string | null): Model | null {
  if (saved === null) return null;
  try {
    const model = Model.fromJson({ ...(JSON.parse(saved) as IJsonModel), global: LAYOUT_SETTINGS });
    const places = tabsOf(model).map(tabPlace);
    if (places.length === 0 || places.some((place) => place === null)) return null;
    for (const place of places) lastOpened = Math.max(lastOpened, place?.opened ?? 0);
    return model;
  } catch {
    return null;
  }
}

export function tabPlace(tab: TabNode): TabPlace | null {
  const config: unknown = tab.getConfig();
  if (typeof config !== "object" || config === null) return null;
  const { route, opened, title, sidebarClosed } = config as Partial<TabPlace>;
  if (typeof route !== "string" || !isAppPath(route) || typeof opened !== "number") return null;
  return { route, opened, ...(typeof title === "string" ? { title } : {}), ...sidebarOf(sidebarClosed !== true) };
}

const sidebarOf = (open: boolean): Pick<TabPlace, "sidebarClosed"> => (open ? {} : { sidebarClosed: true });

/** The tabs in layout order: groups left to right, top to bottom, each group's tabs in strip order. */
export function tabsOf(model: Model): TabNode[] {
  const tabs: TabNode[] = [];
  model.visitNodes((node) => { if (node instanceof TabNode) tabs.push(node); });
  return tabs;
}

function groupsOf(model: Model): TabSetNode[] {
  const groups: TabSetNode[] = [];
  model.visitNodes((node) => { if (node instanceof TabSetNode) groups.push(node); });
  return groups;
}

export function tabById(model: Model, id: TabId | null | undefined): TabNode | null {
  const node = id == null ? undefined : model.getNodeById(id);
  return node instanceof TabNode ? node : null;
}

function activeGroup(model: Model): TabSetNode | null {
  return model.getActiveTabset() ?? model.getFirstTabSet() ?? null;
}

/** The tab the user works in: the active group's selected tab. */
export function activeTab(model: Model): TabNode | null {
  const selected = activeGroup(model)?.getSelectedNode();
  return selected instanceof TabNode ? selected : null;
}

function groupOf(tab: TabNode): TabSetNode | null {
  const parent = tab.getParent();
  return parent instanceof TabSetNode ? parent : null;
}

export function addTab(group: TabSetNode, route: AppPath, id: TabId = newTabId()): { readonly id: TabId; readonly action: Action } {
  return { id, action: Actions.addTab(tabJson(id, route), group.getId(), DockLocation.CENTER, -1, true) };
}

/** A tab on `route` in the group of `tab`. */
export function addTabNextTo(model: Model, tab: TabNode, route: AppPath): { readonly id: TabId; readonly action: Action } | null {
  const group = groupOf(tab) ?? activeGroup(model);
  return group === null ? null : addTab(group, route);
}

/** A tab on `route` in the active group, the first group when none is active; `id` reopens a closed tab as itself. */
export function addTabToActiveGroup(model: Model, route: AppPath, id?: TabId): { readonly id: TabId; readonly action: Action } | null {
  const group = activeGroup(model);
  return group === null ? null : addTab(group, route, id);
}

/** The group beside `group`: the next in layout order, else the one before; `undefined` when it is alone. */
function groupBeside(model: Model, group: TabSetNode): TabSetNode | undefined {
  const groups = groupsOf(model);
  const index = groups.indexOf(group);
  return groups[index + 1] ?? (index > 0 ? groups[index - 1] : undefined);
}

/**
 * Shows `route` beside `from`, in the group beside it, creating one to the right if there is
 * none; a tab beside already on it is focused. One action, so one change. On a wide screen the
 * caller's tab stays the one worked in, as WA-10's second pane did; on a narrow one, which
 * shows only the group worked in, the tab just shown becomes it, or the opened page would stay
 * hidden (E1).
 */
export function showBeside(model: Model, from: TabNode, route: AppPath, narrow: boolean): Action | null {
  const anchor = groupOf(from) ?? model.getFirstTabSet();
  if (anchor === undefined) return null;
  const open = tabsOf(model).find((tab) => groupOf(tab) !== anchor && tabPlace(tab)?.route === route);
  const target = groupBeside(model, anchor);
  const id = newTabId();
  const shown =
    open !== undefined ? Actions.selectTab(open.getId())
    : target !== undefined ? Actions.addTab(tabJson(id, route), target.getId(), DockLocation.CENTER, -1, true)
    : Actions.addTab(tabJson(id, route), anchor.getId(), DockLocation.RIGHT, -1, true);
  // Selecting a tab makes its group the one worked in.
  const workedIn = narrow ? Actions.selectTab(open?.getId() ?? id) : Actions.setActiveTabset(anchor.getId());
  return Actions.group([shown, workedIn]);
}

/** The tab menu's "Move to the side": into the group beside, or a new group to the right; `null` for a tab alone in the only group. */
export function movedBeside(model: Model, tab: TabNode): Action | null {
  const group = groupOf(tab);
  if (group === null) return null;
  const target = groupBeside(model, group);
  if (target !== undefined) return Actions.moveNode(tab.getId(), target.getId(), DockLocation.CENTER, -1, true);
  return group.getChildren().length > 1 ? Actions.moveNode(tab.getId(), group.getId(), DockLocation.RIGHT, -1, true) : null;
}

/** The other tabs in the group of `tab`, for the tab menu's "Close others". */
export function othersInGroup(tab: TabNode): TabId[] {
  return (groupOf(tab)?.getChildren() ?? []).filter((node) => node !== tab).map((node) => node.getId());
}

/** The tab showing exactly `route`, the active one first. */
export function tabShowing(model: Model, route: AppPath): TabNode | null {
  const active = activeTab(model);
  if (active !== null && tabPlace(active)?.route === route) return active;
  return tabsOf(model).find((tab) => tabPlace(tab)?.route === route) ?? null;
}

/** The newest open tab whose route `matches` (Q3). */
export function newestTab(model: Model, matches: (route: AppPath) => boolean): TabNode | null {
  let newest: { tab: TabNode; opened: number } | null = null;
  for (const tab of tabsOf(model)) {
    const place = tabPlace(tab);
    if (place !== null && matches(place.route) && (newest === null || place.opened > newest.opened)) {
      newest = { tab, opened: place.opened };
    }
  }
  return newest?.tab ?? null;
}

/** The tab now shows `route`; its title stays while the page stays, only its query or fragment moved. */
export function movedTo(tab: TabNode, route: AppPath): Action {
  const place = tabPlace(tab);
  const samePage = place !== null && pathnameOf(place.route) === pathnameOf(route);
  const config: TabPlace = {
    route,
    opened: place?.opened ?? openedNow(),
    ...(samePage && place.title !== undefined ? { title: place.title } : {}),
    ...sidebarOf(place?.sidebarClosed !== true),
  };
  return Actions.updateNodeAttributes(tab.getId(), { config, name: config.title ?? route });
}

export function titled(tab: TabNode, title: string | null): Action | null {
  const place = tabPlace(tab);
  if (place === null || (place.title ?? null) === title) return null;
  const config: TabPlace = { route: place.route, opened: place.opened, ...(title === null ? {} : { title }), ...sidebarOf(place.sidebarClosed !== true) };
  return Actions.updateNodeAttributes(tab.getId(), { config, name: title ?? place.route });
}

/** The tab's sidebar opens or closes; the tab keeps it in its place, so a restored tab has it as it was. */
export function sidebarShown(tab: TabNode, open: boolean): Action | null {
  const place = tabPlace(tab);
  if (place === null || (place.sidebarClosed !== true) === open) return null;
  const config: TabPlace = { route: place.route, opened: place.opened, ...(place.title === undefined ? {} : { title: place.title }), ...sidebarOf(open) };
  return Actions.updateNodeAttributes(tab.getId(), { config });
}

export const selectTab = (id: TabId): Action => Actions.selectTab(id);
export const deleteTab = (id: TabId): Action => Actions.deleteTab(id);

/** The tab a close action is about, or `null` for any other action. */
export function closedTabOf(action: Action): TabId | null {
  return action.type === Actions.DELETE_TAB && typeof action.data["node"] === "string" ? action.data["node"] : null;
}

/** Narrow screens show one group, the active one, at full width (WA-10). */
export function oneGroupShown(model: Model, narrow: boolean): Action | null {
  const maximized = model.getMaximizedTabset();
  const group = activeGroup(model);
  if (narrow && group !== null && maximized !== group && groupsOf(model).length > 1) return Actions.maximizeToggle(group.getId());
  if (!narrow && maximized !== undefined) return Actions.maximizeToggle(maximized.getId());
  return null;
}

export type { Action as LayoutAction, Model as TabLayoutModel, TabNode } from "flexlayout-react";
